import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'swtpa_dev_tools_visible';

/**
 * Hook to manage dev tools visibility.
 * - Default: OFF (so you can focus on UI/UX)
 * - Toggle: Ctrl+Shift+D
 * - Persists to localStorage
 */
export function useDevTools() {
  const [showDevTools, setShowDevTools] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  const toggle = useCallback(() => {
    setShowDevTools(prev => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEY, String(newValue));
      console.log(`🛠️ Dev tools ${newValue ? 'ENABLED' : 'DISABLED'} (Ctrl+Shift+D to toggle)`);
      return newValue;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+D (or Cmd+Shift+D on Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { showDevTools, toggle };
}

// Singleton for components that can't use hooks
let globalShowDevTools = typeof window !== 'undefined'
  ? localStorage.getItem(STORAGE_KEY) === 'true'
  : false;

export function getDevToolsVisible() {
  return globalShowDevTools;
}

// Listen for storage changes
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      globalShowDevTools = e.newValue === 'true';
    }
  });
}
